import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import DangerIcon from '@shared/components/Icons/Danger';
import useBannerContent from '@shared/queries/useBannerContent';
import { BannerContent } from '@shared/types/contentful';
import { HorizonAlertTone } from '@shared/types/horizon';
import type {} from '@shared/utilities/horizon/components';
import { useMutation } from '@tanstack/react-query';

import styles from './Banner.module.scss';

const Banner = () => {
    const contentType = 'auctionsBroadcastBanner';

    const { data, isLoading, isError } = useBannerContent(contentType);

    // Use of entry type as third parameter to avoid TS errors in dismissBanner.
    const mutation = useMutation<unknown, unknown, BannerContent, unknown>({
        mutationKey: [contentType],
    });

    const getIcon = (tone: HorizonAlertTone) => {
        switch (tone) {
            case HorizonAlertTone.Critical:
                return <DangerIcon />;
            case HorizonAlertTone.Caution:
                return <hzn-icon-warning></hzn-icon-warning>;
            case HorizonAlertTone.Positive:
                return <hzn-icon-check></hzn-icon-check>;
            case HorizonAlertTone.Flagged:
            case HorizonAlertTone.Info:
            case HorizonAlertTone.Neutral:
            default:
                return <hzn-icon-info-outline></hzn-icon-info-outline>;
        }
    };

    if (isLoading || isError) {
        return <></>;
    }

    const newestEntry = data?.sort(
        (a, b) => Date.parse(b.sys.updatedAt) - Date.parse(a.sys.updatedAt)
    )[0]?.fields;

    // If there's no entry, its dismissed, or it doesn't have content.
    if (
        !newestEntry ||
        newestEntry.dismissed ||
        !(newestEntry.bannerMessage && newestEntry.severity)
    ) {
        return <></>;
    }

    const dismissBanner = () => {
        if (newestEntry) {
            newestEntry.dismissed = true;
            mutation.mutate(newestEntry);
        }
    };

    return (
        <div
            id="notification-banner"
            className={`${styles['banner-container']} ${styles[newestEntry.severity]}`}>
            <div className={styles['banner']}>
                <div className={styles['icon']}>{getIcon(newestEntry?.severity)}</div>
                <p>{newestEntry?.bannerMessage}</p>
                <span className={`${styles['close-button']}`}>
                    <IconButton aria-label="close" onClick={dismissBanner}>
                        <CloseIcon />
                    </IconButton>
                </span>
            </div>
        </div>
    );
};

export default Banner;
